/**
 * Briefing — the founder briefing the Cowork task publishes every weekday.
 *
 * /briefing/daily shows the latest published briefing; ?date=YYYY-MM-DD
 * deep-links a day. The stored HTML is a complete self-contained document,
 * so it renders in a sandboxed <iframe srcdoc> (scripts allowed for its
 * charts, no same-origin access to the admin app) instead of being injected
 * into this page's DOM.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import EmptyState from '../common/EmptyState';
import { APP_BAR_HEIGHT } from '../../theme/layoutConstants';
import {
  BriefingKind,
  BriefingListItem,
  BriefingRecord,
  fetchBriefing,
  fetchBriefingList,
  formatBriefingDate,
  pngDownloadUrl,
} from './api';

const PAGE_KINDS: BriefingKind[] = ['daily', 'weekly'];
const TITLES: Record<string, string> = { daily: 'Daily briefing', weekly: 'Weekly briefing' };

const BriefingPage: React.FC = () => {
  const params = useParams<{ kind?: string }>();
  const kind = (params.kind || 'daily') as BriefingKind;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedDate = searchParams.get('date');

  const [items, setItems] = useState<BriefingListItem[] | null>(null);
  const [record, setRecord] = useState<BriefingRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingRecord, setLoadingRecord] = useState(false);

  const validKind = PAGE_KINDS.includes(kind);

  useEffect(() => {
    if (!validKind) return;
    let cancelled = false;
    setItems(null);
    setError(null);
    fetchBriefingList(kind)
      .then((list) => { if (!cancelled) setItems(list); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [kind, validKind]);

  // Newest first. A ?date that is not a stored date falls back to the latest.
  const selectedIndex = useMemo(() => {
    if (!items || !items.length) return -1;
    if (requestedDate) {
      const i = items.findIndex((it) => it.date === requestedDate);
      if (i >= 0) return i;
    }
    return 0;
  }, [items, requestedDate]);
  const selected = selectedIndex >= 0 && items ? items[selectedIndex] : null;

  useEffect(() => {
    if (items && requestedDate && selectedIndex === 0 && items[0]?.date !== requestedDate) {
      const next = new URLSearchParams(searchParams);
      next.delete('date');
      setSearchParams(next, { replace: true });
    }
  }, [items, requestedDate, selectedIndex, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selected) { setRecord(null); return undefined; }
    let cancelled = false;
    setLoadingRecord(true);
    fetchBriefing(selected.id)
      .then((r) => { if (!cancelled) setRecord(r); })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoadingRecord(false); });
    return () => { cancelled = true; };
  }, [selected]);

  const goTo = useCallback((date: string) => {
    const next = new URLSearchParams(searchParams);
    if (items && items[0] && items[0].date === date) next.delete('date');
    else next.set('date', date);
    setSearchParams(next, { replace: true });
  }, [items, searchParams, setSearchParams]);

  if (!validKind) {
    return <EmptyState title="Unknown briefing" description={`There is no "${kind}" briefing. Open the daily briefing instead.`} />;
  }

  const fullHeight = `calc(100vh - ${APP_BAR_HEIGHT}px)`;
  const older = items && selectedIndex >= 0 && selectedIndex < items.length - 1 ? items[selectedIndex + 1] : null;
  const newer = items && selectedIndex > 0 ? items[selectedIndex - 1] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: fullHeight }}>
      <Paper square elevation={0} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {TITLES[kind]}{selected ? ` · ${formatBriefingDate(selected.date)}` : ''}
            </Typography>
            {selected && (
              <Typography variant="body2" color="text.secondary" noWrap title={selected.headline}>
                {selected.headline}
              </Typography>
            )}
          </Box>
          {items && items.length > 0 && selected && (
            <>
              <Tooltip title={older ? `Previous: ${formatBriefingDate(older.date)}` : 'No earlier briefing'}>
                <span>
                  <IconButton aria-label="Previous day" size="small" disabled={!older} onClick={() => older && goTo(older.date)}>
                    <ChevronLeftIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Select
                size="small"
                value={selected.date}
                onChange={(e) => goTo(String(e.target.value))}
                inputProps={{ 'aria-label': 'Briefing date' }}
                sx={{ minWidth: 200 }}
              >
                {items.map((it) => (
                  <MenuItem key={it.id} value={it.date}>{formatBriefingDate(it.date)}</MenuItem>
                ))}
              </Select>
              <Tooltip title={newer ? `Next: ${formatBriefingDate(newer.date)}` : 'This is the latest briefing'}>
                <span>
                  <IconButton aria-label="Next day" size="small" disabled={!newer} onClick={() => newer && goTo(newer.date)}>
                    <ChevronRightIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} href={pngDownloadUrl(selected.id)} download>
                Download PNG
              </Button>
              {(record?.links?.artifact_url || selected.artifact_url) && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  href={record?.links?.artifact_url || selected.artifact_url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Claude
                </Button>
              )}
            </>
          )}
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: 'background.default' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>Could not load the briefing: {error}</Alert>}
        {!error && items === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>
        )}
        {!error && items && items.length === 0 && (
          <EmptyState
            title="The daily briefing has not been published yet"
            description="The Cowork task publishes it each weekday morning with POST /api/briefing/publish. To check the data feed, call GET /api/briefing/snapshot with the briefing token."
          />
        )}
        {!error && record && (
          <iframe
            key={record.id}
            title={`${TITLES[kind]} ${record.date}`}
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            srcDoc={record.html}
            style={{ border: 0, width: '100%', height: '100%', display: 'block', background: '#fff', opacity: loadingRecord ? 0.5 : 1 }}
          />
        )}
      </Box>
    </Box>
  );
};

export default BriefingPage;
